import {
  GlobalPermission,
  ProfileSchema,
  SystemPermission,
  SystemPermission_Permission,
  User,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';

import { create } from '@bufbuild/protobuf';
import { RolesModel, UserRole } from '~/shared/models/system/RolesModel';
import { ProfileModel } from '~/shared/models/auth-user/ProfileModel';
import { assertIsDefined } from '~/types/assert-type';
import { ResourceNameIdConverter } from '~/shared/models/ResourceNameIdConverter';
import {
  createDashboardRoute,
  createRoutes,
} from '~/shared/models/create-routes';

export class AuthUserModel {
  readonly email: string;

  private readonly globalPermissions: Set<GlobalPermission>;

  readonly profile: ProfileModel;

  private roleBySystemId: Map<string, UserRole>;

  private permissionsBySystemId: Map<string, Set<SystemPermission_Permission>>;

  readonly systemsWithAdminRole: Set<string>;

  readonly canSeeIssuesList: boolean;

  readonly canSeeSystemsDropdown: boolean;

  readonly canScanDeviceQRCode: boolean;

  readonly defaultRedirectRoute: string;

  readonly isInstaller: boolean;

  constructor(user: User) {
    this.email = user.email;
    assertIsDefined(user.profile);
    const displayName = user.profile.displayName || user.email.split('@')[0];

    this.globalPermissions = new Set(user.globalPermissions);

    this.roleBySystemId = new Map(
      user.systemPermissions.map((systemPermission) => [
        ResourceNameIdConverter.systemNameToId(systemPermission.systemName),
        RolesModel.permissionsToRole(systemPermission.permissions),
      ]),
    );

    this.isInstaller = Array.from(this.roleBySystemId.values()).some(
      (role) => role === 'installer',
    );

    this.profile = new ProfileModel(
      create(ProfileSchema, { ...user.profile, displayName }),
    );

    this.permissionsBySystemId = new Map(
      user.systemPermissions.map((systemPermission) => [
        ResourceNameIdConverter.systemNameToId(systemPermission.systemName),
        new Set(systemPermission.permissions),
      ]),
    );

    this.systemsWithAdminRole = new Set(
      user.systemPermissions
        .filter((systemPermission) => {
          const role = RolesModel.permissionsToRole(
            systemPermission.permissions,
          );
          return role === 'admin' || role === 'internal_admin';
        })
        .map((systemPermission) => systemPermission.systemName),
    );

    this.canSeeSystemsDropdown = this.permissionsBySystemId.size > 1;

    this.canSeeIssuesList = AuthUserModel.canSeeIssuesList(
      user.systemPermissions,
    );

    this.canScanDeviceQRCode = AuthUserModel.canScanDeviceQRCode(
      user.systemPermissions,
    );

    this.defaultRedirectRoute = this.createDefaultRedirectRoute();
  }

  shouldShowSystemsList() {
    return (
      this.hasGlobalPermission(GlobalPermission.CREATE_SYSTEM) ||
      this.permissionsBySystemId.size !== 1
    );
  }

  hasRoleForSystemId(role: UserRole, systemId: string) {
    return this.roleBySystemId.get(systemId) === role;
  }

  hasPermissionForSystemId(
    permission: SystemPermission_Permission,
    systemId: string,
  ) {
    const permissions = this.permissionsBySystemId.get(systemId);

    if (!permissions) {
      return false;
    }

    return permissions.has(permission);
  }

  hasGlobalPermission(permission: GlobalPermission) {
    return this.globalPermissions.has(permission);
  }

  private static canSeeIssuesList(systemPermissions: SystemPermission[]) {
    const allPermissions = new Set(
      systemPermissions.flatMap(
        (systemPermission) => systemPermission.permissions,
      ),
    );

    return allPermissions.has(SystemPermission_Permission.COMMISSIONING);
  }

  private static canScanDeviceQRCode(systemPermissions: SystemPermission[]) {
    const allPermissions = new Set(
      systemPermissions.flatMap(
        (systemPermission) => systemPermission.permissions,
      ),
    );

    return allPermissions.has(SystemPermission_Permission.COMMISSIONING);
  }

  private createDefaultRedirectRoute() {
    if (this.shouldShowSystemsList()) {
      return createRoutes.Systems;
    }

    const systemId = Array.from(this.permissionsBySystemId.keys())[0];

    return createDashboardRoute(systemId);
  }
}
